import { type HTMLAttributes, type ReactNode, type Ref } from "react";

import { useMediaQuery } from "../../hooks";
import { cn, evaluate } from "../../utils";
import { Button, type ButtonProps } from "../Button";
import { Icon, type IconProps } from "../Icon";
import { Typography } from "../Typography";

export type AlertVariant = "error" | "warning" | "notice" | "success";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  title: string;
  variant?: AlertVariant;
  description?: ReactNode | string;
  iconProps?: Omit<IconProps, "name">;
  closeIconProps?: Omit<IconProps, "name">;
  actionProps?: ButtonProps;
  titleClassName?: string;
}

export const Alert = ({
  title,
  variant = "error",
  description,
  className,
  iconProps,
  closeIconProps,
  actionProps,
  titleClassName,
  ref,
  ...props
}: AlertProps) => {
  const { isSm } = useMediaQuery();

  const isError = variant === "error";
  const isWarning = variant === "warning";
  const isNotice = variant === "notice";
  const isSuccess = variant === "success";
  const isDescriptionString = typeof description === "string";

  const textColor = cn({
    "ui:text-error": isError,
    "ui:text-warning": isWarning,
    "ui:text-notice": isNotice,
    "ui:text-success": isSuccess,
  });

  const icon = evaluate({
    notice: isNotice,
    "navigation-2": isSuccess,
    attention: isError || isWarning,
  });

  const closeIcon = closeIconProps && (
    <Icon
      size="md"
      name="cross"
      hoverEffect="circle"
      aria-label="Close alert"
      {...closeIconProps}
    />
  );

  const shouldRenderActions = Boolean(actionProps || (isSm && closeIconProps));

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        "ui:flex ui:flex-col ui:items-center ui:justify-between ui:gap-3 ui:rounded-sm ui:border ui:p-2 ui:sm:flex-row",
        {
          "ui:border-error ui:bg-[linear-gradient(90deg,rgba(255,86,98,0.10)_0%,rgba(153,51,59,0.00)_100%)]":
            isError,
          "ui:border-warning": isWarning,
          "ui:border-notice": isNotice,
          "ui:border-success": isSuccess,
        },
        className,
      )}
      {...props}
    >
      <div className="ui:flex ui:items-center ui:gap-3">
        {icon && (
          <Icon
            name={icon}
            {...iconProps}
            className={cn(
              "ui:grid ui:size-8 ui:min-w-8 ui:place-items-center ui:rounded-sm",
              textColor,
              {
                "ui:bg-error/10": isError,
                "ui:bg-warning/10": isWarning,
                "ui:bg-notice/10": isNotice,
                "ui:bg-success/10": isSuccess,
              },
              iconProps?.className,
            )}
          />
        )}

        <div className="ui:grid">
          <Typography bold className={cn(textColor, titleClassName)}>
            {title}
          </Typography>
          {isDescriptionString ? (
            <Typography primary size={12}>
              {description}
            </Typography>
          ) : (
            description
          )}
        </div>

        {!isSm && closeIcon}
      </div>
      {shouldRenderActions && (
        <div className="ui:flex ui:w-full ui:items-center ui:gap-3 ui:sm:w-auto">
          {actionProps && (
            <Button
              {...actionProps}
              variant="secondary"
              size={isSm ? "lg" : "md"}
              className={cn(
                "ui:w-full ui:animate-fade ui:px-8 ui:text-[15px] ui:whitespace-nowrap ui:animate-once",
                actionProps.className,
              )}
            >
              {actionProps.children ?? "Confirm"}
            </Button>
          )}

          {isSm && closeIcon}
        </div>
      )}
    </div>
  );
};
